// QR Kiosk v2 — KV2-T1 route guard. Replaces the bare AuthorizedRoute that wrapped the
// v2 machine tree (login-only). Four outcomes, three of them BYTE-IDENTICAL to
// AuthorizedRoute (SEC-REQ-KV2G-4):
//   1. authenticated + role ∈ PUBLIC_KIOSK_ROLES → <Outlet /> (kiosk, unchanged)
//   2. authenticated, wrong role                 → defaultRoute()  (unchanged)
//   3. UNAUTHENTICATED on machines/:id (the QR)  → GuestEntryV2 chooser, URL unchanged (NEW)
//   4. unauthenticated on any OTHER path         → company sign-in redirect (unchanged)
//
// The gating DECISION reads accountContext.user (localStorage) exactly as AuthorizedRoute
// does. We ALSO subscribe to s.account.user purely as a re-render trigger: after a guest
// PIN redeem stores the token + dispatches refreshAccount, this gate re-renders on the
// SAME URL and outcome (1) opens the kiosk — no navigation to a different route needed.
import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import accountContext from "../../../account/accountContext";
import { Roles } from "../../../account/models/AccountModels";
import { buildAccountSignInWithReturn } from "../../../account/routes";
import useDefaultRedirect from "../../../account/hooks/useDefaultRedirect";
import { useAppSelector } from "../../../shared/hooks/sharedHooks";
import { menuInternalNumber } from "../shared/kioskNav";
import GuestEntryV2 from "./GuestEntryV2";

export function KioskEntryGate() {
  const location = useLocation();
  const { defaultRoute } = useDefaultRedirect();
  // Reactive dependency ONLY (re-render on login/logout). The decision below stays on
  // accountContext.user so behavior is byte-identical to AuthorizedRoute.
  useAppSelector((s) => s.account.user);

  const fromUrl = `${location.pathname}${location.search}${location.hash}`;
  const hasRole = Roles.PUBLIC_KIOSK_ROLES.includes(accountContext.user?.role || "");

  // (1) authenticated + kiosk role → kiosk, exactly as today
  if (accountContext.user && hasRole) {
    return <Outlet />;
  }

  // (2) authenticated, wrong role → defaultRoute(), exactly as today
  if (accountContext.user && !hasRole) {
    return <Navigate to={defaultRoute()} replace />;
  }

  // (3) unauthenticated on the machine-QR landing → guest chooser, URL unchanged
  const machineInternalNumber = menuInternalNumber(location.pathname);
  if (machineInternalNumber) {
    return <GuestEntryV2 machineInternalNumber={machineInternalNumber} />;
  }

  // (4) unauthenticated on any other v2 path → company sign-in, exactly as today
  return <Navigate to={buildAccountSignInWithReturn(fromUrl)} replace />;
}

export default KioskEntryGate;
